import { useMemo } from 'react'
import { useTheme } from '@mui/styles'
import { useNavigate } from 'react-router'
import config from '@/config'
import lang from '@/lang'
import request from '@/modules/base/util/request'
import { toast } from '@/modules/base/components/toast/toastConfig'
import type { AiWidgetRuntime, WidgetTheme } from '@ai-chat-widget-contracts/contracts'
import { defaultStrings } from '@ai-chat-widget/default-strings'
import contextualHelp from './contextual-help.json'
import { resolveAiWidgetLink, withAiWidgetExternalTracking } from './resolve-link'
import moment from 'moment'

export const useAiWidgetRuntime = (): AiWidgetRuntime => {
  const theme = useTheme<WidgetTheme>()
  const navigate = useNavigate()
  return useMemo(() => {
    const open = (href: string) => {
      const resolution = resolveAiWidgetLink(href, window.location.href)

      switch (resolution.kind) {
        case 'internal':
          navigate(resolution.target)
          break
        case 'external':
          window.open(withAiWidgetExternalTracking(resolution.target), '_blank', 'noopener,noreferrer')
          break
        case 'telephone':
        case 'email':
          window.location.href = resolution.target
          break
      }
    }

    return {
      hostMode: 'embedded',
      requestFn: request,
      toastApi: toast,
      endpoints: {
        conversations: `${config.resourceHost}ai/chat/conversations`,
        quotaStatus: `${config.resourceHost}ai/chat/quota/status`,
        transformText: `${config.resourceHost}ai/transform/text`,
        transcriptions: `${config.resourceHost}ai/speech/transcriptions`,
        messageFeedback: (messageId: string) => `${config.resourceHost}ai/chat/messages/${messageId}/feedback`,
        jobCancel: (jobId: string) => `${config.resourceHost}ai/chat/jobs/${jobId}/cancel`,
      },
      speech: {
        locale: 'de-DE',
        transcriptionTimeoutMs: config.requestExtraLongTimeout,
        maxRecordingSeconds: 60,
      },
      links: {
        faq: config.faqUrl,
        supportTicket: config.supportTicketUrl,
        open,
      },
      quickActions: [
        {
          id: 'products',
          icon: 'products',
          title: 'Produkte entdecken',
          description: 'Erfahre mehr über unsere Lösungen',
          onSelect: () => open(config.aiChatProductUrl),
        },
        {
          id: 'support',
          icon: 'support',
          title: 'Hilfe & Support',
          description: 'Antworten auf Fragen und Unterstützung',
          onSelect: () => open(config.faqUrl),
        },
        {
          id: 'appointment',
          icon: 'appointment',
          title: 'Termin vereinbaren',
          description: 'Persönliche Beratung oder Demo',
          onSelect: () => open('https://www.orgamax.de/service/kaufberatung/'),
        },
        {
          id: 'phone',
          icon: 'phone',
          title: 'Mit jemandem sprechen',
          description: 'Jetzt mit einem Mitarbeiter telefonieren',
          onSelect: () => open(`tel:${lang.salesTelephoneNumber}`),
        },
      ],
      contextualHelp: contextualHelp as AiWidgetRuntime['contextualHelp'],
      debug: {
        enabled: ENV.build !== 'production',
        metadata: {
          product: ENV.product,
          build: ENV.build,
          releaseStage: ENV.releaseStage,
          version: ENV.version,
        },
      },
      dateTimeFormat: config.datetimeFormat.dateTime24,
      formatDateTime: value => moment(value).format(config.datetimeFormat.dateTime24),
      formatRelativeTime: value => moment(value).fromNow(),
      strings: defaultStrings,
      theme,
    }
  }, [theme, navigate])
}
